// ProductFetchSection.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Collapse,
  // IconButton,
  TextareaAutosize,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Code as CodeIcon, Article as ArticleIcon, TableChart as TableIcon } from '@mui/icons-material';
import axios from 'axios';

// Helper to display formatted content
const DisplayFormattedContent = React.memo(({ content, format }) => {
  const isHtmlContent = format === 'table' && typeof content === 'string' && content.trim().startsWith('<');

  if (isHtmlContent) {
    return (
      <Box
        dangerouslySetInnerHTML={{ __html: content }}
        sx={{
          mt: 1,
          p: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflowX: 'auto',
          '& table': { borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' },
          '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
          '& th': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#424242' : '#f2f2f2', fontWeight: 'bold' },
        }}
      />
    );
  }

  // For JSON or other text content (including summary)
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f9f9f9',
        maxHeight: 450,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        mt: 1,
      }}
    >
      {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
    </Paper>
  );
});

const ProductFetchSection = ({ showSnackbar }) => {
  const [productId, setProductId] = useState('');
  const [productData, setProductData] = useState(null);
  const [isFetchingProduct, setIsFetchingProduct] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [format, setFormat] = useState('json'); // Default to 'json'
  const [aiFormattedOutput, setAiFormattedOutput] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [aiError, setAiError] = useState('');

  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const handleFetchProduct = useCallback(async () => {
    if (!productId.trim()) {
      showSnackbar('Please enter a Product ID', 'warning');
      return;
    }

    setIsFetchingProduct(true);
    setProductData(null);
    setAiFormattedOutput('');
    setFetchError('');
    setAiError('');
    setFormat('json'); // Reset to JSON format on new fetch

    try {
      // Simulate API delay for testing loading states
      // await new Promise(resolve => setTimeout(resolve, 1500));
      const res = await axios.get(`/api/v1/google-merchant/get-single-product/${productId.trim()}`);
      const fetchedData = res.data?.data || res.data;

      if (!fetchedData || (typeof fetchedData === 'object' && Object.keys(fetchedData).length === 0)) {
        const msg = `No product data found for ID: ${productId.trim()}`;
        setFetchError(msg);
        showSnackbar(msg, 'warning');
        setProductData(null);
      } else {
        setProductData(fetchedData);
        showSnackbar('✅ Product fetched successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to fetch product:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch product.';
      setFetchError(errorMsg);
      showSnackbar(`❌ ${errorMsg}`, 'error');
      setProductData(null);
    } finally {
      setIsFetchingProduct(false);
    }
  }, [productId, showSnackbar]);

  const handleProcessWithAI = useCallback(async () => {
    if (!productData && !customPrompt.trim()) {
      showSnackbar('No product data or custom prompt to process.', 'warning');
      return;
    }
    if (format === 'json' && !customPrompt.trim()) {
        // If format is JSON and no custom prompt, we show raw data, no AI call needed
        setAiFormattedOutput(''); // Clear any previous AI output
        return;
    }


    setIsProcessingAI(true);
    setAiFormattedOutput('');
    setAiError('');

    try {
      // Simulate API delay
      // await new Promise(resolve => setTimeout(resolve, 2000));
      const payload = {
        format: customPrompt.trim() ? null : format, // If custom prompt, backend might ignore format or use it as context
        data: productData,
        customPrompt: customPrompt.trim() || null,
      };

      const response = await axios.post('/api/v1/ai/ai-format', payload);
      setAiFormattedOutput(response.data.result);
      if (format !== 'json' || customPrompt.trim()) {
        showSnackbar(`✅ Content processed by AI.`, 'success');
      }
    } catch (err) {
      console.error('AI formatting failed:', err);
      const errorMsg = err.response?.data?.error || err.message || 'AI formatting failed.';
      const errorDetails = err.response?.data?.details;
      setAiError(errorDetails ? `${errorMsg} Details: ${JSON.stringify(errorDetails)}` : errorMsg);
      showSnackbar(`❌ ${errorMsg}`, 'error');
    } finally {
      setIsProcessingAI(false);
    }
  }, [productData, format, showSnackbar, customPrompt]);


  // Determine what content to display
  const displayContent = (format === 'json' && !customPrompt.trim() && productData)
    ? productData // Show raw productData if format is 'json' and no custom prompt
    : aiFormattedOutput; // Otherwise, show AI output (or if custom prompt was used)


  // Automatically trigger AI if format changes and it's not JSON, and data exists
  useEffect(() => {
    if (productData && format !== 'json' && !customPrompt.trim()) {
      handleProcessWithAI();
    }
    // If switching to JSON, clear AI output.
    if (format === 'json' && !customPrompt.trim()) {
        setAiFormattedOutput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, productData]); // customPrompt is handled by the button click

  const getFormatIcon = (fmt) => {
    switch(fmt) {
        case 'json': return <CodeIcon />;
        case 'table': return <TableIcon />;
        case 'summary': return <ArticleIcon />;
        default: return null;
    }
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, maxWidth: 800, margin: 'auto' }}>
      <Stack spacing={3}>
        <Typography variant="h5" component="h2" fontWeight="bold" color="primary.main" gutterBottom>
          Product AI Formatter
        </Typography>

        {/* Step 1: Fetch Product */}
        <Box component="section" sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
            1. Fetch Product by ID
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              fullWidth
              label="Product ID"
              placeholder="e.g., online:en:US:product_123"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={isFetchingProduct || isProcessingAI}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleFetchProduct}
              disabled={isFetchingProduct || !productId.trim() || isProcessingAI}
              startIcon={isFetchingProduct ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ minWidth: '150px', height: '56px', textTransform: 'none', fontSize: '0.9rem' }}
            >
              {isFetchingProduct ? 'Fetching...' : 'Fetch Product'}
            </Button>
          </Stack>
          {fetchError && <Alert severity="error" sx={{ mt: 2 }}>{fetchError}</Alert>}
        </Box>

        {/* Step 2: Process with AI (visible only if productData exists) */}
        <Collapse in={Boolean(productData) || showCustomPrompt} timeout="auto">
            <Box component="section" sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1, mt: productData ? 0 : 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
                2. Configure AI Processing
                </Typography>
                <Stack spacing={2}>
                <FormControl fullWidth>
                    <InputLabel id="format-select-label">Output Format</InputLabel>
                    <Select
                    labelId="format-select-label"
                    value={format}
                    label="Output Format"
                    onChange={(e) => setFormat(e.target.value)}
                    disabled={isProcessingAI || isFetchingProduct}
                    startAdornment={
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            {getFormatIcon(format)}
                        </Box>
                    }
                    >
                    <MenuItem value="json"><CodeIcon sx={{mr:1}} /> Raw JSON</MenuItem>
                    <MenuItem value="table"><TableIcon sx={{mr:1}} /> HTML Table (AI)</MenuItem>
                    <MenuItem value="summary"><ArticleIcon sx={{mr:1}} /> Summary (AI)</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="text"
                    onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                    startIcon={<ExpandMoreIcon sx={{ transform: showCustomPrompt ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.secondary' }}
                    disabled={isProcessingAI || isFetchingProduct}
                >
                    {showCustomPrompt ? 'Hide Custom Prompt' : 'Use Custom Prompt'}
                </Button>

                <Collapse in={showCustomPrompt} timeout="auto">
                    <TextareaAutosize
                    minRows={3}
                    placeholder="Enter your custom AI prompt here. If data is fetched, it will be appended."
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    disabled={isProcessingAI || isFetchingProduct}
                    style={{
                        width: '100%',
                        padding: '10px',
                        fontFamily: 'inherit',
                        fontSize: '0.95rem',
                        borderRadius: '4px',
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        resize: 'vertical'
                    }}
                    />
                </Collapse>

                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleProcessWithAI}
                    disabled={
                        isProcessingAI ||
                        isFetchingProduct ||
                        (!productData && !customPrompt.trim()) || // Disabled if no data AND no custom prompt
                        (format === 'json' && !customPrompt.trim()) // Disabled for JSON format if no custom prompt
                    }
                    startIcon={isProcessingAI ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ minWidth: '200px', height: '56px', textTransform: 'none', fontSize: '0.9rem' }}
                >
                    {isProcessingAI ? 'Processing...' : (customPrompt.trim() ? 'Process with Custom Prompt' : `Format as ${format}`)}
                </Button>
                </Stack>
                {aiError && <Alert severity="error" sx={{ mt: 2 }}>{aiError}</Alert>}
            </Box>
        </Collapse>

        {/* Output Section */}
        {(productData || aiFormattedOutput) && (
          <Box component="section" sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              { (format === 'json' && !customPrompt.trim() && productData)
                ? 'Product Data (Raw JSON):'
                : customPrompt.trim()
                ? 'AI Output (Custom Prompt):'
                : `AI Generated ${format.charAt(0).toUpperCase() + format.slice(1)}:`}
            </Typography>
            <DisplayFormattedContent
              content={displayContent}
              format={customPrompt.trim() ? 'text' : format} // If custom prompt, treat output as text unless explicitly table
            />
          </Box>
        )}

        {!productData && !isFetchingProduct && !fetchError && productId && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Product data will appear here once fetched. If AI processing is selected, it will be formatted below.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default ProductFetchSection;

// --- Example of how to use ProductFetchSection with a Snackbar provider ---
//
// 1. Install notistack: `npm install notistack` or `yarn add notistack`
//
// 2. In your App.js or main application file:
//
// import React from 'react';
// import { SnackbarProvider, useSnackbar } from 'notistack';
// import ProductFetchSection from './ProductFetchSection'; // Adjust path as needed
// import { CssBaseline, Container, ThemeProvider, createTheme } from '@mui/material';

// const theme = createTheme(); // Your Material UI theme

// function AppContent() {
//   const { enqueueSnackbar } = useSnackbar();

//   const showSnackbar = (message, variant = 'info') => { // default variant to 'info'
//     enqueueSnackbar(message, { variant });
//   };

//   return (
//     <Container maxWidth="md" sx={{ py: 4 }}>
//       <ProductFetchSection showSnackbar={showSnackbar} />
//     </Container>
//   );
// }

// export default function App() {
//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <SnackbarProvider
//         maxSnack={3}
//         anchorOrigin={{
//           vertical: 'bottom',
//           horizontal: 'right',
//         }}
//       >
//         <AppContent />
//       </SnackbarProvider>
//     </ThemeProvider>
//   );
// }